import { useEffect, useRef } from 'react'; // <--- Import useRef
import PropTypes from 'prop-types';

// MUI Core Components
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";

// MUI Icons
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

// MUI DataGrid Components
import { 
  GridToolbarContainer, 
  GridToolbarColumnsButton, 
  GridToolbarFilterButton, 
  GridToolbarExport, 
  GridToolbarDensitySelector,
  useGridApiContext,
  useGridSelector, // <--- 1. Import Selector Hook
} from '@mui/x-data-grid';

export default function CustomToolbar({ 
  setSearchText, 
  searchText, 
  canSearch, 
  hasFilters, 
  openFiltersModal,
  onDensityChange 
}) {
  const apiRef = useGridApiContext();
  
  // 2. Select the density value directly from the grid state.
  //    This hook automatically subscribes to store updates.
  //    It works like a Redux selector.
  const densityValue = useGridSelector(apiRef, (state) => state.density.value);

  // Ref to skip the initial mount save if desired (optional, but good for performance)
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the first render to avoid double-saving initial state
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // 3. Trigger callback whenever densityValue changes
    if (onDensityChange) {
      onDensityChange(densityValue);
    }
  }, [densityValue, onDensityChange]);

  return (
    <GridToolbarContainer sx={{ 
      p: 1, 
      display: 'flex', 
      justifyContent: 'space-between',
      alignItems: 'center',
    }}>
      {/* LEFT SIDE: Standard DataGrid Tools */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </Box>

      {/* RIGHT SIDE: Custom Search & Filter Button */}
      {(canSearch || hasFilters) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          
          {/* Custom Filter Button */}
          {hasFilters && (
            <Tooltip title="Add Custom Filters">
              <IconButton 
                onClick={openFiltersModal} 
                size="small" 
                color="primary" 
                sx={{ mr: 1 }}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Search Bar */}
          {canSearch && (
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                width: '200px',
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.875rem', 
                  backgroundColor: 'background.paper',
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchText ? (
                  <InputAdornment position="end">
                    <IconButton 
                      size="small" 
                      onClick={() => setSearchText('')}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          )}
        </Box>
      )}
    </GridToolbarContainer>
  );
}

CustomToolbar.propTypes = {
  setSearchText: PropTypes.func.isRequired,
  searchText: PropTypes.string,
  canSearch: PropTypes.bool,
  hasFilters: PropTypes.bool,
  openFiltersModal: PropTypes.func,
  onDensityChange: PropTypes.func,
};